/**
 * Gift Modal — Cart API Operations
 *
 * Handles saving gift config changes to the Shopify cart via /cart/change.js,
 * refreshing cart drawer sections, and applying section HTML updates.
 *
 * Used by GiftBoxCustomizerModal when editing a gift note from the cart drawer.
 * Loaded before gift-box-customizer-modal.js so the modal can delegate cart
 * operations to window.SnowcubesGiftCart.
 */
(function initGiftModalCart(global) {
  'use strict';

  var Gift = global.SnowcubesGift || {};

  /**
   * Save gift config changes to the Shopify cart.
   * @param {object} opts
   * @param {string} opts.itemKey - Cart item key (preferred identifier)
   * @param {number} opts.variantId - Variant ID (fallback identifier)
   * @param {object} opts.config - Gift config object
   * @param {number} [opts.timeoutMs=10000] - Abort timeout
   * @param {object} [opts.callbacks] - UI callback functions
   * @param {function} [opts.callbacks.onLoadingStart]
   * @param {function} [opts.callbacks.onLoadingEnd]
   * @param {function} [opts.callbacks.onError]
   * @param {function} [opts.callbacks.onSuccess]
   * @returns {Promise<{ok: boolean, error?: string}>}
   */
  async function saveChangesToCart(opts) {
    var itemKey = opts.itemKey;
    var variantId = opts.variantId;
    var config = opts.config;
    var timeoutMs = opts.timeoutMs || 10000;
    var callbacks = opts.callbacks || {};

    if (callbacks.onLoadingStart) callbacks.onLoadingStart();

    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, timeoutMs);

    try {
      // Fetch fresh cart to get current item key
      var cartResponse = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' },
        signal: controller.signal,
      });
      if (!cartResponse.ok) throw new Error('Failed to fetch cart');

      var cart = await cartResponse.json();

      // Find item — prefer itemKey, fall back to variantId + config presence
      var item;
      if (itemKey) {
        item = cart.items.find(function (i) { return i.key === itemKey; });
      }
      if (!item && variantId) {
        // Only fall back to variant matching when it's UNAMBIGUOUS. If 2+ line items
        // share this variant AND a gift config, editing by variant alone could
        // overwrite the WRONG note — leave `item` unset so we bail to the refresh
        // path below rather than corrupt a different customer's gift.
        var matches = cart.items.filter(function (i) {
          return i.variant_id === variantId && i.properties && i.properties['_GiftCardConfig'];
        });
        if (matches.length === 1) item = matches[0];
      }

      if (!item) {
        clearTimeout(timeoutId);
        if (callbacks.onLoadingEnd) callbacks.onLoadingEnd();
        var msg = 'This item is no longer in your cart. Please refresh your cart and try again.';
        if (callbacks.onError) callbacks.onError(msg);
        // Best effort: refresh cart drawer
        try { await refreshCartDrawerSections(); } catch (_) { /* swallow */ }
        return { ok: false, error: 'item_not_found' };
      }

      // Merge new config with existing properties
      var existingProps = item.properties || {};
      var updatedProps = Object.assign({}, existingProps, {
        '_GiftCardConfig': JSON.stringify(config),
        '_GiftCardTo': config.to || '',
        '_GiftCardFrom': config.from || '',
        '_GiftCardMessage': config.message || '',
      });

      // Update cart
      var updateResponse = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          id: item.key,
          quantity: item.quantity,
          properties: updatedProps,
        }),
      });

      if (!updateResponse.ok) throw new Error('Failed to update cart');

      clearTimeout(timeoutId);
      if (callbacks.onLoadingEnd) callbacks.onLoadingEnd();
      if (callbacks.onSuccess) callbacks.onSuccess();

      // Refresh cart drawer
      try { await refreshCartDrawerSections(); } catch (_) { /* swallow */ }

      return { ok: true };

    } catch (error) {
      clearTimeout(timeoutId);
      if (callbacks.onLoadingEnd) callbacks.onLoadingEnd();

      var message = "Something went wrong — your note wasn't saved. Please try again.";
      if (error.name === 'AbortError') {
        message = 'That took longer than expected. Please check your connection and try again.';
      }

      if (callbacks.onError) callbacks.onError(message);
      return { ok: false, error: error.message || 'unknown' };
    }
  }

  /**
   * Refresh cart drawer and cart bubble sections via Shopify's section rendering API.
   * @returns {Promise<void>}
   */
  async function refreshCartDrawerSections() {
    var sections = ['cart-bubble', 'cart-drawer'];
    var base = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
    var url = base + '?sections=' + sections.join(',');
    var response = await fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) {
      throw new Error('Failed to refresh cart sections');
    }
    var sectionsHtml = await response.json();
    applyCartSections(sectionsHtml);
  }

  /**
   * Apply section HTML updates to the cart drawer and cart bubble DOM elements.
   * @param {object} sectionsHtml - Shopify sections response { 'cart-drawer': html, 'cart-bubble': html }
   */
  function applyCartSections(sectionsHtml) {
    var drawerHtml = sectionsHtml && sectionsHtml['cart-drawer'];
    if (drawerHtml) {
      var cartDrawerSection = document.querySelector('[data-section="cart-drawer"]');
      if (cartDrawerSection) cartDrawerSection.innerHTML = drawerHtml;

      var dialog1 = document.getElementById('dialog-cart-1');
      var dialog1Body = dialog1 && dialog1.querySelector('.dialog__content__body');
      if (dialog1Body) dialog1Body.innerHTML = drawerHtml;

      var dialog2 = document.getElementById('dialog-cart-2');
      var dialog2Body = dialog2 && dialog2.querySelector('.dialog__content__body');
      if (dialog2Body) dialog2Body.innerHTML = drawerHtml;
    }

    var bubbleHtml = sectionsHtml && sectionsHtml['cart-bubble'];
    if (bubbleHtml) {
      var countMatch = bubbleHtml.match(/\d+/);
      var countText = countMatch ? countMatch[0] : '';
      var bubbleSticky = document.getElementById('cart-details--sticky');
      var bubbleRelative = document.getElementById('cart-details--relative');
      if (bubbleSticky) bubbleSticky.innerHTML = countText;
      if (bubbleRelative) bubbleRelative.innerHTML = countText;
    }

    var renderRoot =
      document.querySelector('[data-section="cart-drawer"]') ||
      (document.getElementById('dialog-cart-1') && document.getElementById('dialog-cart-1').querySelector('.dialog__content__body')) ||
      (document.getElementById('dialog-cart-2') && document.getElementById('dialog-cart-2').querySelector('.dialog__content__body'));

    // Re-render gift note summaries if core is loaded
    Gift = global.SnowcubesGift || {};
    if (Gift.renderGiftSummaries) {
      Gift.renderGiftSummaries(renderRoot || undefined);
    }
  }

  var api = {
    saveChangesToCart: saveChangesToCart,
    refreshCartDrawerSections: refreshCartDrawerSections,
    applyCartSections: applyCartSections,
  };

  global.SnowcubesGiftCart = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
